<script lang="ts" setup>
import { useRouter, useRoute } from 'vue-router'
import { onMounted, ref } from 'vue'
import { useNotificationsStore } from '@/store/notifications'
import { usePayrollChange } from '@/app/core/composable/routes/useChangePayroll'
import { notificationError } from '@/app/core/util/notificationUtils'
import { useField, useForm } from 'vee-validate'
import { useI18n } from 'vue-i18n'
import { object, string, number } from 'yup'
import ESelect from '../components/ESelect.vue'
import ETextField from '../components/ETextField.vue'
import EMultipleSelect from '../components/ESelectMultiple.vue'
import Editor from '../components/ETextEditor.vue'
import {
  DTextField,
  DLoading,
  Settings01Icon,
  File06Icon,
  DBtn,
  DToggleField
} from 'front-library'
import {
  createTemplate,
  updateTemplate,
  getTemplate
} from '../../../../app/core/services/receipt-templates'
usePayrollChange(() =>
  Boolean(route.params.id) ? back() : window.location.reload()
)

/* router, notifications */
const notifications = useNotificationsStore()
const router = useRouter()
const route = useRoute()
const { t } = useI18n()

/* Datos */
const dataTemplate = ref<any>({
  printConfig: {
    orientation: 'vertical',
    sheetSize: 'A4'
  },
  isStandard: false
})
const isDisabledComponents = ref<boolean>(false)
const itemsPaperSize = ref([{ id: 'A4', name: 'A4' }])
const itemsPaperOrientation = ref([
  { id: 'vertical', name: t('receiptTemplates.create.vertical') },
  { id: 'horizontal', name: t('receiptTemplates.create.horizontal') }
])
const optionsSelectMultiple = ref<object>({})
const isHeader = ref(false)
const isBody = ref(true)
const isFooter = ref(false)

/* Datos componente Loading */
const loading = ref(false)

/* Esquema de validacion campos obligatorios y no obligatorios */
const validationSchema = object().shape({
  code: string().required(
    t('form.valid.fieldRequired', { fields: t('fields.code') })
  ),
  name: string().required(
    t('form.valid.fieldRequired', { fields: t('fields.name') })
  ),
  bodyTemplate: string().required(
    t('form.valid.fieldRequired', { fields: 'body' })
  ),
  printConfig: object({
    marginTop: number()
      .transform(value => (isNaN(value) ? undefined : value))
      .nullable()
      .required(t('form.valid.fieldRequired', { fields: 'top' })),
    marginBottom: number()
      .transform(value => (isNaN(value) ? undefined : value))
      .nullable()
      .required(t('form.valid.fieldRequired', { fields: 'bottom' })),
    marginLeft: number()
      .transform(value => (isNaN(value) ? undefined : value))
      .nullable()
      .required(t('form.valid.fieldRequired', { fields: 'left' })),
    marginRight: number()
      .transform(value => (isNaN(value) ? undefined : value))
      .nullable()
      .required(t('form.valid.fieldRequired', { fields: 'right' }))
  })
})

/* Uso del metodo de validaión, retorno de errores y metodo para reiniciar formulario */
const { handleSubmit, errors } = useForm({
  initialValues: dataTemplate.value,
  validationSchema
})

/* Definicion de  campos del formulario */
const { value: bodyTemplate } = useField<string>('bodyTemplate')
const { value: headerTemplate } = useField<string>('headerTemplate')
const { value: footerTemplate } = useField<string>('footerTemplate')
const { value: marginTop } = useField<number>('printConfig.marginTop')
const { value: marginBottom } = useField<number>(
  'printConfig.marginBottom'
)
const { value: marginLeft } = useField<number>('printConfig.marginLeft')
const { value: marginRight } = useField<number>('printConfig.marginRight')
const { value: sheetSize } = useField<string>('printConfig.sheetSize')
const { value: orientation } = useField<string>('printConfig.orientation')
const { value: code } = useField<string>('code')
const { value: name } = useField<string>('name')
const { value: isStandard } = useField<boolean>('isStandard')
const valueMultipleSelect = ref<any>({})

/* Metodo que valida los campos obligatorios del formulario */
const onSubmit = handleSubmit(values => {
  const { companies, payrolls, process } = valueMultipleSelect.value
  values.companies = companies
  values.payrolls = payrolls
  values.process = process
  void createEditTemplate(values)
})

/* Crear plantilla */
const createEditTemplate = async (values: any) => {
  try {
    if (!route.params.id) {
      await createTemplate(values)
      notifications.addNotification({
        title: t('notification.created'),
        text: t('receiptTemplates.notification.action', [
          t('notification.wasCreated')
        ]),
        type: 'success',
        time: 10000
      })
    } else {
      await updateTemplate(values, route.params.id as string)
      notifications.addNotification({
        title: t('notification.edited'),
        text: t('receiptTemplates.notification.action', [
          t('notification.wasEdited')
        ]),
        type: 'success',
        time: 10000
      })
    }
    void router.push({ name: 'ListReceiptTemplates' })
  } catch (error: any) {
    notificationError(error, 'receiptTemplates', t, notifications, loading)
  }
}

const viewEditor = (value: string) => {
  switch (value) {
    case 'header':
      isHeader.value = true
      isBody.value = false
      isFooter.value = false
      break
    case 'body':
      isBody.value = true
      isHeader.value = false
      isFooter.value = false
      break
    case 'footer':
      isFooter.value = true
      isBody.value = false
      isHeader.value = false
      break

    default:
      break
  }
}

/* Volver */
const back = () => {
  void router.push({ name: 'ListReceiptTemplates' })
}

const validateIsStandard = () => {
  valueMultipleSelect.value = {
    companies: [],
    payrolls: []
  }
}

const getEntity = async () => {
  if (!route.params.id) return
  dataTemplate.value = await getTemplate(route.params.id as string)
  valueMultipleSelect.value = {
    companies: dataTemplate.value?.companies ?? [],
    payrolls: dataTemplate.value?.payrolls ?? [],
    process: dataTemplate.value?.process ?? []
  }
}

onMounted(async () => {
  try {
    await getEntity()
  } catch (error: any) {
    notificationError(error, 'receiptTemplates', t, notifications, loading)
  }
})
</script>
<template>
  <DLoading v-show="loading" message="" />
  <div class="flex flex-col h-full scroll w-full px-3">
    <div style="min-height: 100px" class="bg-gray-50 h-28">
      <div class="leading-4 pt-responsive ml-5">
        <p
          class="text-3xl font-semibold text-shadow text-blue-900"
          v-html="t('receiptTemplates.create.title')" />
        <!-- {{ route.params.id == undefined ? 'Crear' : 'Editar' }} -->
      </div>
    </div>
    <div class="flex-grow w-full scroll overflow-y-auto">
      <!-- Diseño hoja -->
      <div class="bg-gray-50 mr-auto ml-auto my-3 flex flex-col items-center">
        <div
          size="A4"
          layout="portrait"
          class="bg-white shadow-xl"
          :style="{
            padding: `${marginTop ?? 0}cm ${marginRight ?? 0}cm ${
              marginBottom ?? 0
            }cm ${marginLeft ?? 0}cm`
          }">
          <div class="h-full w-full flex flex-col">
            <div
              class="relative w-full min-h-fit bg-gray-100 cursor-pointer"
              @click.prevent="viewEditor('header')">
              <Editor
                v-model="headerTemplate"
                class="h-32"
                style="min-height: 8rem"
                :class="{ 'blur-sm': !isHeader }" />
              <div
                v-show="!isHeader"
                class="absolute w-full h-full top-0 left-0 z-40 flex justify-center items-center bg-transparent">
                <h1
                  class="text-center"
                  v-html="t('receiptTemplates.create.header')" />
              </div>
            </div>
            <div
              class="py-5 w-full flex-grow"
              @click.prevent="viewEditor('body')">
              <Editor
                v-model="bodyTemplate"
                :error="errors.bodyTemplate"
                class="w-full" />
            </div>
            <div
              class="relative w-full min-h-fit bg-gray-100 cursor-pointer"
              @click.prevent="viewEditor('footer')">
              <Editor
                v-model="footerTemplate"
                class="h-32"
                style="min-height: 8rem"
                :class="{ 'blur-sm': !isFooter }" />
              <div
                v-show="!isFooter"
                class="absolute w-full h-full top-0 left-0 z-40 flex justify-center items-center bg-transparent">
                <h1
                  class="text-center"
                  v-html="t('receiptTemplates.create.footer')" />
              </div>
            </div>
          </div>
        </div>
      </div>
      <!-- config -->
      <div
        class="paper-configuration fixed right-10 top-1/3 border w-80 p-3 rounded-3xl bg-white z-30">
        <section class="flex flex-row justify-center gap-2 mb-3">
          <Settings01Icon class="text-gray-500" />
          <p
            class="font-semibold"
            v-html="t('receiptTemplates.create.setting')" />
        </section>
        <section>
          <h1
            class="text-gray-400 mb-3 text-sm"
            v-html="t('receiptTemplates.create.size')" />
          <div class="inline-flex gap-1 mb-3">
            <ESelect :options="itemsPaperSize" v-model="sheetSize" />
            <ESelect :options="itemsPaperOrientation" v-model="orientation" />
          </div>
        </section>
        <!-- MARGENES -->
        <section>
          <h1
            class="text-gray-400 mb-6 text-sm"
            v-html="t('receiptTemplates.create.margins')" />
          <div>
            <div class="flex justify-center mb-3">
              <ETextField
                v-model="marginTop"
                :error="errors['printConfig.marginTop']" />
            </div>
            <div class="flex justify-center gap-2 mb-3 mt-3 items-center">
              <ETextField
                v-model="marginLeft"
                :error="errors['printConfig.marginLeft']" />
              <File06Icon class="w-10 h-10 text-black" />
              <ETextField
                v-model="marginRight"
                :error="errors['printConfig.marginRight']" />
            </div>
            <div class="flex justify-center">
              <ETextField
                v-model="marginBottom"
                :error="errors['printConfig.marginBottom']" />
            </div>
          </div>
        </section>
      </div>
    </div>
    <!-- footer -->
    <div style="z-index: 40" class="h-24 w-full mb-2 rounded-lg px-3 bg-white">
      <div class="flex h-full gap-7 py-4">
        <div class="col-span-1">
          <DTextField
            v-model="code"
            :error="errors.code"
            :label="t('fields.code')" />
        </div>
        <div class="col-span-1">
          <DTextField
            v-model="name"
            :error="errors.name"
            :label="t('fields.name')" />
        </div>
        <div class="col-span-1">
          <EMultipleSelect
            :label="t('receiptTemplates.create.appliesTo')"
            v-model="valueMultipleSelect"
            :disabled="
              isStandard
                ? (isDisabledComponents = true)
                : (isDisabledComponents = false)
            "
            :options="optionsSelectMultiple" />
        </div>
        <div class="self-center col-span-1">
          <DToggleField
            :label="t('receiptTemplates.create.standard')"
            v-model="isStandard"
            @change="validateIsStandard" />
        </div>
        <div class="self-center grow mr-8 flex justify-end gap-2">
          <DBtn
            color="secondary"
            @click="back"
            v-html="t('form.button.back')" />
          <DBtn @click="onSubmit" v-html="t('form.button.save')" />
        </div>
      </div>
    </div>
  </div>
</template>
<style scoped>
div[size='A4'][layout='portrait'] {
  background-color: white;
  display: block;
  margin: 0 auto;
  width: 29.7cm;
  max-width: 21cm;
  height: 31cm;
}
</style>
